import type { ApolloQueryResult } from '@apollo/client';
import { createContext, useContext } from 'react';

import type { FiatCurrency, Scalars } from '#src/__generated__/globalTypes.ts';
import type { MonetaryAmountParams } from '#src/lib/monetaryAmount.ts';

import type {
  ConfigQuery,
  ConfigQuery_currentLocation,
  ConfigQuery_currentUser,
  ConfigQuery_exchangeRate,
} from './types.ts';

export interface AlgoliaCardIndexes {
  // blockchain cards indices
  New: string;
  'Ending Soon': string;
  'Newly Listed': string;
  'Highest Price': string;
  'Lowest Price': string;
  'Highest Average Score': string;
  'Best Value': string;
  'Popular Player': string;

  // On sale cards indices
  'Cards On Sale Ending Soon': string;
  'Cards On Sale Newly Listed': string;
  'Cards On Sale Highest Price': string;
  'Cards On Sale Lowest Price': string;
  'Cards On Sale Highest Average Score': string;
  'Cards On Sale Best Value': string;
  'Cards On Sale Popular Player': string;
  'Cards On Sale Bids Count': string;
}

export interface AlgoliaIndexes extends AlgoliaCardIndexes {
  User: string;
  Player: string;
  Club: string;
  League: string;
  Country: string;
  'National Team': string;
  Competition: string;
}

export type AlgoliaIndex = keyof AlgoliaIndexes;

export type AlgoliaCardIndexesName = keyof AlgoliaCardIndexes;

type Distribute<T> = T extends string ? `ASC-${T}` | `DESC-${T}` : never;

export type PrintablePlayerSortName = Distribute<
  | 'L5'
  | 'L10'
  | 'L10AA'
  | 'L10D'
  | 'L15'
  | 'L15AA'
  | 'L15D'
  | 'season_avg'
  | 'L40'
  | 'name_for_sorting'
  | 'next_classic_fixture_projected_score'
  | 'next_classic_fixture_projected_grade'
  | 'next_daily_fixture_projected_score'
  | 'next_daily_fixture_projected_grade'
  | 'gain_on_daily_projection'
  | 'gain_on_classic_projection'
  | 'points'
  | 'assists'
  | 'rebounds'
  | 'blocks'
  | 'double_double'
  | 'average_mins_played'
  | 'total_mins_played'
  | 'steals'
  | 'three_points_made'
  | 'triple_double'
  | 'turnovers'
  | 'total_set_piece_taken'
  | 'total_goals'
  | 'total_goal_assist'
  | 'total_assist_penalty_won'
  | 'total_last_man_tackle'
  | 'total_clean_sheet'
  | 'total_clean_sheet_60'
  | 'total_yellow_card'
  | 'total_red_card'
  | 'total_error_lead_to_goal'
  | 'total_penalty_conceded'
  | 'total_three_goals_conceded'
  | 'total_own_goals'
  | 'total_game_started'
  | 'total_won_tackle'
  | 'total_interception_won'
  | 'total_accurate_pass'
  | 'average_set_piece_taken'
  | 'average_goals'
  | 'average_goal_assist'
  | 'average_assist_penalty_won'
  | 'average_last_man_tackle'
  | 'average_clean_sheet'
  | 'average_clean_sheet_60'
  | 'average_yellow_card'
  | 'average_red_card'
  | 'average_error_lead_to_goal'
  | 'average_penalty_conceded'
  | 'average_three_goals_conceded'
  | 'average_own_goals'
  | 'average_three_points_made'
  | 'average_game_started'
  | 'average_won_tackle'
  | 'average_interception_won'
  | 'average_accurate_pass'
  | 'floor_prices_in_season_limited'
  | 'floor_prices_in_season_rare'
  | 'floor_prices_in_season_super_rare'
  | 'floor_prices_in_season_unique'
  | 'floor_prices_all_seasons_limited'
  | 'floor_prices_all_seasons_rare'
  | 'floor_prices_all_seasons_super_rare'
  | 'floor_prices_all_seasons_unique'
  | 'ib_prices_limited'
  | `starter_odds_basis_points`
  | 'last_thirty_days_subscription_count'
  | 'hitting_at_base'
  | 'hitting_at_plate'
  | 'hitting_caught_stealing'
  | 'hitting_doubles'
  | 'hitting_hit_by_pitch'
  | 'hitting_home_runs'
  | 'hitting_runs'
  | 'hitting_runs_batted_in'
  | 'hitting_singles'
  | 'hitting_stolen_bases'
  | 'hitting_strikeouts'
  | 'hitting_triples'
  | 'hitting_walks'
  | 'pitching_earned_runs'
  | 'pitching_hits_allowed'
  | 'pitching_hit_batsmen'
  | 'pitching_holds'
  | 'pitching_innings_pitched'
  | 'pitching_no_hitters'
  | 'pitching_pitch_count'
  | 'pitching_relief_appearance'
  | 'pitching_saves'
  | 'pitching_strikeouts'
  | 'pitching_walks'
  | 'pitching_wins'
  | `projected_grade${string}`
>;

export type PrintableI18nPlayerSortName =
  | 'DESC-L10'
  | 'DESC-L15'
  | Distribute<'player_name' | 'name_for_sorting' | 'name_for_sorting'>;

export type GallerySortName =
  | 'Cards New'
  | 'Cards Highest Average Score'
  | 'Cards Highest Price'
  | 'Cards Lowest Price'
  | 'Cards Player Name'
  | 'Sealing Value'
  | 'Tier';

export type GallerySortNames = GallerySortName[];

// TODO(sylvain): Need to figure a way to support dynamic fields
export type ShortSortName = string;

export type AlgoliaCardIndexesNames = AlgoliaCardIndexesName[];

type SolanaAddress = Nullable<Scalars['SolanaAddress']['output']>;

type ConfigContext = {
  algoliaIndexes: AlgoliaIndexes;
  algoliaCardIndexes: AlgoliaCardIndexes;
  algoliaApplicationId: string;
  algoliaSearchApiKey: string;
  ethereumNetworkId: string;
  ethereumEndpoint: string;
  sorareEncryptionKey: string;
  solanaBankAuthority: SolanaAddress;
  ethereumBankDepositAddress: EthereumAddress;
  baseBankAddress: EthereumAddress;
  baseCrossDomainDepositAddress: EthereumAddress;
  l1MessengerAddress: EthereumAddress;
  ethQuantum: bigint;
  starkExchangeAddress: EthereumAddress;
  ethereumGatewayMinterProxyAddress: EthereumAddress;
  so5: {
    currentManagerHasLiveGames: boolean;
    myAvailableFixtureStory: ConfigQuery['so5']['myAvailableFixtureStory'];
    teamsPlayingNextClassicGameWeeks: ConfigQuery['config']['so5']['teamsPlayingNextClassicGameWeeks'];
    teamsPlayingNextDailyGameWeeks: ConfigQuery['config']['so5']['teamsPlayingNextDailyGameWeeks'];
    so5Competitions: ConfigQuery['so5']['so5Competitions'];
    inSeasonName: string;
    allSeasonsName: string;
    cardMaxGrade: number;
    dailyTasksExpiresAt: Date | null;
  };
  wheel: {
    tasks: ConfigQuery['market']['wheelTasks'];
    commonWheelConfig: ConfigQuery['market']['commonWheelConfig'];
    limitedWheelConfig: ConfigQuery['market']['limitedWheelConfig'];
    rareWheelConfig: ConfigQuery['market']['rareWheelConfig'];
  };
  activeRevealableCardCampaigns: ConfigQuery['config']['activeRevealableCardCampaigns'];
  tasks: ConfigQuery['market']['tasks'];
  currentUser: ConfigQuery_currentUser | null;
  refetch: () => Promise<ApolloQueryResult<ConfigQuery>>;
  loading: boolean;
  updateQuery: (user: ConfigQuery_currentUser) => void;
  exchangeRate: ConfigQuery_exchangeRate & {
    getEthRate: (to: FiatCurrency) => number;
  };
  marketFeeRateBasisPoints: number;
  marketFeePercentagePoints: bigint;
  idealDepositFeesAmountMinor: number;
  mangopayVisaFeesBasisPoints: string;
  mangopayAmexFeesBasisPoints: string;
  paypalDepositFeesBasisPoints: number;
  mangopayApplePayDepositFeesBasisPoints: number;
  stripeCreditCardFeesBasisPoints: string;
  defaultFiatCurrency: FiatCurrency;
  minimumReceiveWeiAmount: bigint;
  currentLocation: ConfigQuery_currentLocation;
  promotionalEvents: ConfigQuery['tokens']['promotionalEvents'];
  counts: ConfigQuery['config']['counts'];
  paypalMinimumBidAmount: MonetaryAmountParams;
  referralProgramConfig: ConfigQuery['config']['referralProgramConfig'];
  cardSet: ConfigQuery['cardSet'];
};

export const ConfigContext = createContext<ConfigContext | null>(null);

export const useConfigContext = () => useContext(ConfigContext)!;
